import React from "react";

import {Radio, RadioGroup} from "@headlessui/react";
import {bindActionCreators} from "@reduxjs/toolkit";

import * as auth from "@client/features/auth";
import {notify} from "@client/features/notifications";
import * as orm from "@client/features/orm";
import {diets, useCalculator} from "@client/features/planner";

import * as dates from "@client/dates";
import * as analytics from "@client/shared/analytics";

import {createRouter, routerFactory} from "@client/gotterdammerung";
import {ImperialCaloriesContext} from "@client/site/context";
import * as forms from "@client/site/forms";
import {useAppDispatch, useAppSelector} from "@client/store";
import {classNames, server} from "@reactivated";

export function RadioSelect<
    TEnum extends string,
    TField extends Extract<forms.PFieldHandler<TEnum>, {type: "select"}>,
>(props: {
    field: TField;
    descriptions: Record<TEnum, React.ReactNode | false>;
    labels?: Partial<Record<TEnum, string>>;
    selected: TEnum | null;
}) {
    const filtered = props.field.options
        .map(([setting, label]) => {
            if (setting == null) return null;
            const description: string | React.ReactNode | false =
                props.descriptions[setting];

            if (description == false) {
                return null;
            }
            return {
                setting,
                label,
                description,
            };
        })
        .filter((o) => o != null);

    return (
        <fieldset aria-label="Privacy setting">
            <RadioGroup
                value={props.selected}
                onChange={props.field.onChange}
                className={classNames(
                    "-space-y-px rounded-md",
                    "dark:bg-transparent bg-white",
                )}
            >
                {filtered.map(
                    ({setting, label: defaultLabel, description}, settingIdx) => {
                        const label = props.labels?.[setting] ?? defaultLabel;

                        return (
                            <Radio
                                key={setting}
                                value={setting}
                                aria-label={label}
                                className={classNames(
                                    settingIdx === 0
                                        ? "rounded-tl-md rounded-tr-md"
                                        : "",
                                    settingIdx === filtered.length - 1
                                        ? "rounded-bl-md rounded-br-md"
                                        : "",
                                    "relative flex cursor-pointer items-center focus:outline-hidden border p-4 group",
                                    "dark:border-[#453F63] dark:bg-[#332F4A] dark:data-checked:z-10 dark:data-checked:border-[#7D50FF] dark:data-checked:bg-[#7D50FF]",
                                    "border-gray-200 data-checked:z-10 data-checked:border-indigo-200 data-checked:bg-indigo-50",
                                )}
                            >
                                <span
                                    aria-hidden="true"
                                    className={classNames(
                                        "mt-0.5 flex h-4 w-4 shrink-0 cursor-pointer items-center justify-center rounded-full border",
                                        "border-gray-300 bg-white group-data-checked:border-transparent group-data-checked:bg-indigo-600 group-data-focus:ring-2 group-data-focus:ring-indigo-600 group-data-focus:ring-offset-2",
                                        "dark:border-white dark:group-data-checked:bg-white dark:group-data-focus:ring-[#7D50FF]",
                                    )}
                                >
                                    <span
                                        className={classNames(
                                            "h-1.5 w-1.5 rounded-full",
                                            "dark:bg-[#7D50FF] dark:group-data-checked:opacity-100 dark:opacity-0",
                                            "bg-white",
                                        )}
                                    />
                                </span>
                                <span className="ml-3 flex flex-col gap-0.5">
                                    <span
                                        className={classNames(
                                            "block text-base font-light",
                                            "dark:text-white dark:group-data-checked:text-white",
                                            "text-gray-900 group-data-checked:text-indigo-900",
                                        )}
                                    >
                                        {label}
                                    </span>
                                    <span
                                        className={classNames(
                                            "block text-sm font-light",
                                            "dark:text-white/85 dark:group-data-checked:text-gray-100",
                                            "text-gray-500 group-data-checked:text-indigo-700",
                                        )}
                                    >
                                        {description}
                                    </span>
                                </span>
                            </Radio>
                        );
                    },
                )}
            </RadioGroup>
        </fieldset>
    );
}

export type Input = Record<never, never>;

export type Output = server.core.rpc.Profile.output | null;

type Diet = "LEANGAINS" | "GENERAL" | "KETO";

export const useHooks = ({
    diet,
    requireName,
    profile,
}: {
    diet: Diet;
    requireName?: false;
    profile: server.core.rpc.Profile.output | null;
}) => {
    const registrationForm = auth.useRegistrationForm();
    const dispatch = useAppDispatch();
    const [maxes, setMaxes] = React.useState<
        Record<UUID, {weight: DecimalValue | null; reps: number | null} | null>
    >({
        /*
        [constants.EXERCISE_TYPES.DEADLIFT]: {weight: "102.05828325", reps: 5},
        [constants.EXERCISE_TYPES.SQUAT]: {weight: "102.05828325", reps: 5},
        [constants.EXERCISE_TYPES.PRESS]: {weight: "102.05828325", reps: 5},
        [constants.EXERCISE_TYPES.BENCH]: {weight: "102.05828325", reps: 5},
        */
    });

    const defaultProfile = profile ?? {
        name: "",
        gender: "m" as const,
        activity: "1.2" as const,
        body_fat: null,
        is_imperial: true as const,
        date_of_birth: null,
        weight: null,
        height: null,
    };

    const initial = {
        name: defaultProfile.name,
        gender: (defaultProfile.gender ?? "m") as "m",
        activity: (defaultProfile.activity ?? "1.2") as "1.2",
        body_fat: defaultProfile.body_fat,
        is_imperial: defaultProfile.is_imperial,
        date_of_birth: defaultProfile.date_of_birth ?? null,
        weight: defaultProfile.weight ?? null,
        height: defaultProfile.height ?? null,
    };

    const [settings, setSettings] = React.useState({
        diet,
        activity: "SEDENTARY" as "SEDENTARY" | "ACTIVE" | "VERY_ACTIVE",
        goal: "WEIGHT_LOSS" as
            | "WEIGHT_LOSS"
            | "AGGRESSIVE_WEIGHT_LOSS"
            | "HEALTH"
            | "BULK"
            | "MASS",
    });

    const settingsForm = forms.usePform(
        {
            diet: {
                type: "select",
                options: [
                    ["GENERAL", "No"],
                    ["KETO", "Keto"],
                    ["LEANGAINS", "Leangains"],
                ],
                required: true,
            },
            activity: {
                type: "select",
                options: [
                    ["SEDENTARY", "Sedentary"],
                    ["ACTIVE", "Moderately Active"],
                    ["VERY_ACTIVE", "Very Active"],
                ],
                required: true,
            },
            goal: {
                type: "select",
                options: [
                    ["WEIGHT_LOSS", "Weight loss"],
                    ["AGGRESSIVE_WEIGHT_LOSS", "Aggressive weight loss"],
                    ["HEALTH", "Health"],
                    ["BULK", "Gain muscle"],
                    ["MASS", "Get bigger"],
                ],
                required: true,
            },
        },
        settings,
        setSettings,
    );

    const profileForm = forms.useAutoPform(
        server.journal.nutrition.CalculatorProfileForm,
        initial,
    );

    const prevDietRef = React.useRef(profileForm.values.diet);
    const prevImperialRef = React.useRef(profileForm.values.is_imperial);

    React.useEffect(() => {
        if (prevDietRef.current !== profileForm.values.diet) {
            void analytics.track("CHANGE_CALCULATOR_TYPE", {
                diet: profileForm.values.diet,
            });
            prevDietRef.current = profileForm.values.diet;
        }
        if (prevImperialRef.current !== profileForm.values.is_imperial) {
            void analytics.track("CHANGE_CALCULATOR_IS_IMPERIAL", {
                is_imperial: profileForm.values.is_imperial === true,
            });
            prevImperialRef.current = profileForm.values.is_imperial;
        }
    }, [profileForm.values.diet, profileForm.values.is_imperial]);

    const activity =
        settingsForm.values.activity == "SEDENTARY"
            ? "1.2"
            : settingsForm.values.activity == "ACTIVE"
              ? "1.375"
              : "1.55";

    const variance = {
        WEIGHT_LOSS: "-20",
        AGGRESSIVE_WEIGHT_LOSS: "-35",
        HEALTH: "0",
        BULK: "20",
        MASS: "30",
    }[settingsForm.values.goal];

    const proteinMultiplier = "1.75";

    const liveProfile: server.journal.nutrition.CalculatorProfile.output = {
        body_fat: profileForm.values.body_fat,
        is_imperial: profileForm.values.is_imperial,
        gender: profileForm.values.gender,
        date_of_birth: profileForm.values.date_of_birth,
        height: profileForm.values.height,
        weight: profileForm.values.weight,
        activity,
        use_kilojoules: false,
    };

    const {setIsImperial} = React.useContext(ImperialCaloriesContext);

    React.useEffect(() => {
        setIsImperial(liveProfile.is_imperial);

        return () => {
            setIsImperial(null);
        };
    }, [liveProfile.is_imperial]);

    const general = useCalculator({
        profile: liveProfile,
        formSchema: server.journal.nutrition.GeneralCalculatorForm,
        diet: diets.general,
    });

    React.useEffect(() => {
        general.form.setValues((prev) => ({
            ...prev,
            protein_multiplier: proteinMultiplier,
            variance,
        }));
    }, [proteinMultiplier, variance]);

    const leangains = useCalculator({
        profile: liveProfile,
        formSchema: server.journal.nutrition.LeangainsCalculatorForm,
        diet: diets.leangains,
    });
    const keto = useCalculator({
        profile: liveProfile,
        formSchema: server.journal.nutrition.KetoCalculatorForm,
        diet: diets.keto,
    });

    const selected =
        settingsForm.values.diet == "LEANGAINS"
            ? leangains
            : settingsForm.values.diet == "KETO"
              ? keto
              : general;

    const validatedProfile =
        profileForm.values.date_of_birth != null &&
        dates.isValid(dates.parseISO(profileForm.values.date_of_birth)) &&
        profileForm.values.height != null &&
        profileForm.values.weight != null &&
        (requireName == false || profileForm.values.name != "")
            ? {
                  ...defaultProfile,
                  ...profile,
                  activity,
                  name: profileForm.values.name,
                  date_of_birth: profileForm.values.date_of_birth,
                  weight: profileForm.values.weight,
                  height: profileForm.values.height,
                  gender: profileForm.values.gender,
                  is_imperial: profileForm.values.is_imperial === true,
              }
            : null;

    const age =
        liveProfile.date_of_birth == null
            ? null
            : dates.differenceInYears(
                  new Date(),
                  dates.parseISO(liveProfile.date_of_birth),
              );

    return {
        notify: bindActionCreators({notify}, dispatch).notify,
        age,
        registrationForm,
        requireName,
        maxes,
        setMaxes,
        settingsForm,
        profileForm,
        leangains,
        general,
        selected,
        profile,
        liveProfile,
        validatedProfile,
    };
};

export const InputContext = React.createContext<
    Input & {close: (result: Output) => void}
>(null!);

export const HooksContext = React.createContext<ReturnType<typeof useHooks>>(null!);

export function Hooks(props: {children: React.ReactNode}) {
    const profile = useAppSelector(orm.getProfile);
    const hooks = useHooks({diet: "GENERAL", profile});

    return (
        <HooksContext.Provider value={hooks}>{props.children}</HooksContext.Provider>
    );
}

export const Profile = routerFactory()(() => {
    const input = React.useContext(InputContext);
    const hooks = React.useContext(HooksContext);

    return {input, close: input.close, ...hooks};
})({
    name: "profile",
    path: "",
});

export const Settings = Profile.subroute({
    name: "settings",
    path: "settings/",
});

export const Goal = Settings.subroute({
    name: "goal",
    path: "goal/",
});

export const Results = Goal.subroute({
    name: "results",
    path: "results/",
});

export const router = createRouter([Profile, Settings, Goal, Results], "memory");

export type Router = typeof router;

export function ProfileForm(props: ReturnType<typeof useHooks>) {
    return (
        <div
            className="
                space-y-4
                *:block *:space-y-1
            "
        >
            <h2 className="text-xl">Tell us a little bit about yourself.</h2>

            {props.requireName !== false && (
                <label>
                    <div>What should we call you?</div>
                    <forms.PWidget field={props.profileForm.fields.name} />
                </label>
            )}

            <label>
                <div>When were you born?</div>
                <forms.PWidget field={props.profileForm.fields.date_of_birth} />
            </label>
            <label>
                <div>Do you use metric or imperial units?</div>
                <forms.PWidget field={props.profileForm.fields.is_imperial} />
            </label>
            <label>
                <div>How much do you weigh?</div>
                <forms.PWidget field={props.profileForm.fields.weight} />
            </label>

            <label>
                <div>
                    {props.liveProfile.is_imperial == true
                        ? "How tall are you in inches?"
                        : "How tall are you in centimeters?"}
                </div>
                <forms.PWidget field={props.profileForm.fields.height} />
            </label>

            <label>
                <div>What’s your gender?</div>
                <forms.PWidget field={props.profileForm.fields.gender} />
            </label>

            <label className="hidden!">
                <div>Do you follow a special diet, like keto?</div>
                <forms.PWidget field={props.settingsForm.fields.diet} />
            </label>
        </div>
    );
}

export function ActivityForm(props: ReturnType<typeof useHooks>) {
    return (
        <div
            className="
                space-y-4
                *:block *:space-y-3
            "
        >
            <label>
                <h2 className="text-xl">How active are you?</h2>
                <div>
                    Do not factor in your workouts.
                    <br />
                    You should probably pick <strong>Sedentary</strong>.
                </div>
                <RadioSelect
                    field={props.settingsForm.fields.activity}
                    selected={props.settingsForm.values.activity}
                    descriptions={{
                        SEDENTARY:
                            "You mostly sit around and don't move much throughout the day.",
                        ACTIVE: "You're on your feet most of the day. Your job involves a lot of walking.",
                        VERY_ACTIVE:
                            "You have an intensely physical job like construction. You're moving all day.",
                    }}
                />
            </label>
        </div>
    );
}

const ProfileRoute = Profile.withHooks(() => {})
    .withIndex(ProfileForm)
    .withActions((props) => [
        {
            disabled: props.validatedProfile == null,
            icon: "arrow-right",
            title: "Next",
            onClick: () => {
                router.push("settings.index", {});
            },
        },
    ])
    .withOptions(() => ({
        title: "Profile",
        back: "Journal",
    }));

const SettingsRoute = Settings.withHooks(() => {
    return {};
})
    .withIndex(ActivityForm)
    .withActions(() => [
        {
            icon: "arrow-right",
            title: "Next",
            onClick: () => {
                router.push("goal.index", {});
            },
        },
    ])
    .withOptions(() => ({
        back: {
            locator: router.locator({route: "profile.index", params: {}}),
            title: "Profile",
        },
        title: "Activity level",
    }));

const GoalRoute = Goal.withHooks(() => {})
    .withIndex((props) => (
        <div
            className="
                space-y-4
                *:block *:space-y-3
            "
        >
            <label>
                <h2 className="text-xl">What is your goal?</h2>
                <div>Helps us calculate your caloric needs.</div>
                <RadioSelect
                    field={props.settingsForm.fields.goal}
                    selected={props.settingsForm.values.goal}
                    descriptions={{
                        WEIGHT_LOSS:
                            "Lose at most a pound a week. Mostly from fat. Sustainable long term.",
                        AGGRESSIVE_WEIGHT_LOSS:
                            "Lose more than a pound a week. Maybe two. Experts only. Do not select this.",
                        HEALTH: "Get healthier and slowly replace fat with muscle. Requires working out.",
                        BULK: "Put on muscle with minimal fat. Slow and steady. Requires working out.",
                        MASS: "Get bigger, quickly. Lot of muscle but some fat. Requires working out.",
                    }}
                />
            </label>
        </div>
    ))
    .withActions(() => [
        {
            icon: "arrow-right",
            title: "Next",
            onClick: () => {
                router.push("results.index", {});
            },
        },
    ])
    .withOptions(() => ({
        back: {
            locator: router.locator({route: "settings.index", params: {}}),
            title: "Activity",
        },
        title: "Goal",
    }));

const ResultsRoute = Results.withHooks(() => {})
    .withIndex((props) => (
        <div
            className="
                flex h-full flex-col justify-center gap-8 text-center
                *:block *:space-y-3
            "
        >
            <div className="text-3xl">
                Eat{" "}
                <span className="text-indigo-700">
                    {props.selected.results.targets[0].calories_target}
                </span>{" "}
                calories a day
            </div>
            <div className="text-xl/10 text-pretty">
                Get{" "}
                <span className="text-indigo-700">
                    {Math.round(props.selected.results.targets[0].protein)}
                </span>{" "}
                grams of <span className="text-indigo-700">protein</span>
                <div>
                    With{" "}
                    <span className="text-indigo-700">
                        {Math.round(props.selected.results.targets[0].fat)}
                    </span>{" "}
                    grams of <span className="text-indigo-700">fat</span>
                </div>
                <div>
                    And{" "}
                    <span className="text-indigo-700">
                        {Math.round(props.selected.results.targets[0].carbs)}
                    </span>{" "}
                    grams of <span className="text-indigo-700">carbs</span>
                </div>
            </div>
        </div>
    ))
    .withActions((props) => [
        {
            icon: "repeat",
            title: "Start Over",
            onClick: () => {
                void analytics.track("RESTARTED_MACROS_JOURNEY");
                router.push("profile.index", {});
            },
        },
        {
            icon: "save",
            title: "Save",
            onClick: async () => {
                if (props.validatedProfile == null) {
                    throw new Error("Profile is not valid");
                }

                if (props.profile == null) {
                    throw new Error("Profile is null");
                }
                const targets = props.selected.results.targets.map((target) => ({
                    uuid: crypto.randomUUID(),
                    fiber_target: null,
                    protein_mode: "AT_LEAST" as const,
                    ...target,
                    net_carbs_target: null,
                }));

                const result = await server.journal.day.consumer_planner({
                    targets,
                    user_id: props.profile.user.id,
                    profile: props.validatedProfile,
                    habits: {},
                    assignments: props.selected.results.assignments.reduce<
                        Record<number, UUID>
                    >((acc, assignment, index) => {
                        acc[index] = targets[assignment.reference].uuid;
                        return acc;
                    }, {}),
                });

                if (result.type != "success") {
                    throw new Error("Failed to save consumer planner");
                }
                props.close(result.data);
            },
        },
    ])
    .withOptions(() => ({
        back: {
            locator: router.locator({route: "goal.index", params: {}}),
            title: "Goal",
        },
        title: "Results",
    }));

export const {Route} = router.mount({
    profile: ProfileRoute,
    goal: GoalRoute,
    settings: SettingsRoute,
    results: ResultsRoute,
});
